/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import { getGatewayIdToken } from "./gatewaySession";

// Base URL for the gift API
const giftApiBaseUrl = import.meta.env.VITE_GIFT_API;

const giftApi = axios.create({
  baseURL: giftApiBaseUrl,
});

giftApi.interceptors.request.use(async (config) => {
  const token = getCookie("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${await getGatewayIdToken()}`;
  }
  return config;
});

// Define the necessary interfaces for Gift and GiftProduct

export interface GiftProduct {
  ID: string;
  Quantity: number;
  Value: number;
}

export interface Gift {
  ID?: string;
  DonatorID: string;
  DonatorName: string;
  DonatorEmail: string;
  IsAnonymous: boolean;
  Message: string;
  Products: GiftProduct[];
  GivenDate: string;
  Media?: string;
  GiftTableID: string;
  PaymentID: string;
  PricingToken: string;
  Value?: number;
  ThankYouMessage?: string;
}

// New interface for the sum endpoint result.
export interface GiftSumResult {
  sum: number;
  count: number;
}

// Function to map incoming Gift object to API expected format
const mapGiftToApiFormat = (gift: Gift) => {
  return {
    donator_id: gift.DonatorID,
    donator_name: gift.DonatorName,
    donator_email: gift.DonatorEmail,
    is_anonymous: gift.IsAnonymous,
    message: gift.Message,
    media: gift.Media,
    products: gift.Products.map((product) => ({
      id: product.ID,
      quantity: product.Quantity,
      value: product.Value,
    })),
    given_date: gift.GivenDate,
    gift_table_id: gift.GiftTableID,
    payment_id: gift.PaymentID,
    pricing_token: gift.PricingToken,
    thank_you_message: gift.ThankYouMessage,
  };
};

// Function to map API response to Gift format in PascalCase
const mapApiResponseToGift = (apiResponse: any): Gift => {
  return {
    ID: apiResponse.id,
    DonatorID: apiResponse.donator_id,
    DonatorName: apiResponse.donator_name,
    DonatorEmail: apiResponse.donator_email,
    IsAnonymous: apiResponse.is_anonymous,
    Message: apiResponse.message,
    Products: apiResponse.products.map((product: any) => ({
      ID: product.id,
      Quantity: product.quantity,
      Value: product.value,
    })),
    GivenDate: apiResponse.given_date,
    Media: apiResponse.media,
    GiftTableID: apiResponse.gift_table_id,
    PaymentID: apiResponse.payment_id,
    PricingToken: apiResponse.pricing_token,
    Value: apiResponse.value,
    ThankYouMessage: apiResponse.thank_you_message,
  };
};

// API call to give a gift
export const giveGift = async (gift: Gift): Promise<string> => {
  try {
    const apiFormattedGift = mapGiftToApiFormat(gift);
    const response = await giftApi.post("/api/give-gift", apiFormattedGift);
    return response.data.gift_id;
  } catch (error) {
    console.error("Error giving gift", error);
    throw new Error("Failed to give gift");
  }
};

// API call to create a gift (using the new endpoint)
export const createGift = async (gift: Gift): Promise<string> => {
  try {
    const apiFormattedGift = mapGiftToApiFormat(gift);
    const response = await giftApi.post("/api/gift", apiFormattedGift);
    return response.data.gift_id;
  } catch (error) {
    console.error("Error creating gift", error);
    throw new Error("Failed to create gift");
  }
};

// API call to get a gift by ID
export const getGiftById = async (giftId: string): Promise<Gift> => {
  try {
    const response = await giftApi.get("/api/gift", {
      params: { id: giftId },
    });
    return mapApiResponseToGift(response.data);
  } catch (error) {
    console.error("Error retrieving gift", error);
    throw new Error("Failed to retrieve gift");
  }
};

// API call to update the thank-you message for a gift
export const updateThankYouMessage = async (
  giftId: string,
  thankYouMessage: string
): Promise<void> => {
  try {
    await giftApi.put(`/api/gift/thank-you-message?id=${giftId}`, {
      thank_you_message: thankYouMessage,
    });
  } catch (error) {
    console.error("Error updating thank-you message", error);
    throw new Error("Failed to update thank-you message");
  }
};

// New API call to list gifts by gift table ID (using limit 0 to return them all)
export const listGiftsByGiftTableID = async (
  giftTableId: string,
  idsOnly: boolean = false
): Promise<Gift[] | string[]> => {
  try {
    const params: any = { gift_table_id: giftTableId };
    if (idsOnly) {
      params.ids_only = "true";
    }
    const response = await giftApi.get("/api/gifts/by-gift-table-id", {
      params,
    });
    if (idsOnly) {
      return response.data as string[];
    }
    return response.data.map((apiGift: any) => mapApiResponseToGift(apiGift));
  } catch (error) {
    console.error("Error listing gifts by gift table ID", error);
    throw new Error("Failed to list gifts");
  }
};

// New API call to calculate the sum of gift values and count of gifts by gift table ID.
export const sumGiftsByGiftTableID = async (
  giftTableId: string
): Promise<GiftSumResult> => {
  try {
    const response = await giftApi.get("/api/gifts/sum-by-gift-table-id", {
      params: { gift_table_id: giftTableId },
    });
    // Expect the API response to be { "sum": number, "count": number }
    return response.data as GiftSumResult;
  } catch (error) {
    console.error("Error summing gifts by gift table ID", error);
    throw new Error("Failed to sum gifts");
  }
};
